import type { SpinResponse } from "@slot/client-engine";
import { cannonFire } from "../cannonFixtureHelpers";
import { fx, spinFixture } from "@slot/game-shell";

const mask = (indices: number[]): string =>
  Array.from({ length: 20 }, (_, i) => (indices.includes(i) ? "1" : "0")).join("");

/**
 * Tumbling reels — a TWO-STEP cascade chain. The reveal wins on an L2 cluster (bottom-left);
 * removing it drops the 2 survivors in cols 0,1 down and rains 2 fresh symbols into each — those
 * drop-ins land on the surviving L1s and complete an L1 cluster, which wins the second step. The
 * final collapse produces no win, so the round ends. Each win emits its own
 * update_total_win_meter, so the WinMeter counts 0 → 200 → 500. Cluster/mask wins, no payline.
 *
 * EVERY collapse grid here is the EXACT gravity result of (previous grid − win mask): survivors
 * keep their identity and only fall; fresh symbols appear ONLY in the vacated top cells. That is a
 * hard requirement, not cosmetic — the tumble mechanic treats the fixture grid as authoritative and
 * REBINDS any survivor whose id disagrees with it, so an arbitrary grid makes the surviving symbols
 * visibly morph mid-tumble, silently (the post-run strict pass then matches the rebound grid).
 *
 * Note the chain geometry: only symbols ABOVE a removed cell can fall, so step 2's cluster is
 * placed on rows 1-2 (fresh drop-in + survivor) rather than the very top — that way its removal
 * still makes the row-0 symbols fall.
 */
export function baseFixtureReelTumbleChain(): SpinResponse {
  const win1 = mask([10, 11, 15, 16]);   // L2 ×4 — bottom-left block
  const win2 = mask([5, 6, 10, 11]);     // L1 ×4 — rows 1-2 of cols 0,1 after the first tumble

  return spinFixture("reel-tumble-chain")
    .balance("EUR", "100000")
    // --- seq 0: reveal — L2 cluster wins, removed ---
    .event("reveal", 0, (e) => {
      e.reelSet("default").betAmount("100").accumulatedWinAfter("200").gridRows([
        ["L1", "L1", "L2", "H1", "L3"],
        ["L4", "L5", "L5", "L2", "L5"],
        ["L2", "L2", "L4", "L5", "L2"],
        ["L2", "L2", "L3", "L4", "L2"],
      ]);

      const s = fx.spinBoundaryStart("base:1", "base");
      e.action(s.type, s.payload, { actionPhaseName: s.phase, debugTag: "start" });

      const w = fx.gridWinHighlight({
        positionMask: win1, winAmount: "200", symbolIdentifier: "L2", symbolCount: "4", // 100 × 2
        lines: [{ positionMask: win1, winAmount: "200", baseWinAmount: "100", multiplierApplied: "2" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win1" });

      const fire1 = cannonFire(0);
      e.action(fire1.type, fire1.payload, { actionPhaseName: fire1.phase, debugTag: "cannon_fire1" });

      const r = fx.removeSymbolsByMask(win1);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove1" });

      const t = fx.updateTotalWinMeter("200");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total1" });
    })
    // --- seq 1: collapse — drop-in lands an L1 cluster that wins, removed ---
    .event("collapse", 1, (e) => {
      // Gravity result of seq0 − win1: cols 0,1 keep their 2 survivors (now rows 2-3) and take 2
      // fresh symbols each on rows 0,1 — the row-1 drop-ins land on the surviving L1s at row 2 and
      // form win2. Cols 2,3,4 were untouched by win1, so they are byte-identical to seq0.
      e.reelSet("default").betAmount("100").accumulatedWinAfter("500").gridRows([
        ["L5", "L3", "L2", "H1", "L3"],
        ["L1", "L1", "L5", "L2", "L5"],
        ["L1", "L1", "L4", "L5", "L2"],
        ["L4", "L5", "L3", "L4", "L2"],
      ]);

      const w = fx.gridWinHighlight({
        positionMask: win2, winAmount: "300", symbolIdentifier: "L1", symbolCount: "4", // 100 × 3
        lines: [{ positionMask: win2, winAmount: "300", baseWinAmount: "100", multiplierApplied: "3" }],
      });
      e.action(w.type, w.payload, { actionPhaseName: w.phase, isSkippable: true, debugTag: "win2" });

      const fire2 = cannonFire(1);
      e.action(fire2.type, fire2.payload, { actionPhaseName: fire2.phase, debugTag: "cannon_fire2" });

      const r = fx.removeSymbolsByMask(win2);
      e.action(r.type, r.payload, { actionPhaseName: r.phase, debugTag: "remove2" });

      const t = fx.updateTotalWinMeter("500");
      e.action(t.type, t.payload, { actionPhaseName: t.phase, debugTag: "total2" });
    })
    // --- seq 2: collapse — no further win, round ends ---
    .event("collapse", 2, (e) => {
      // Gravity result of seq1 − win2: removing rows 1-2 of cols 0,1 drops each column's row-0
      // symbol two cells (to row 2) and rains 2 fresh symbols in above it. No further win → end.
      e.reelSet("default").betAmount("100").accumulatedWinAfter("500").gridRows([
        ["L2", "L5", "L2", "H1", "L3"],
        ["L4", "L3", "L5", "L2", "L5"],
        ["L5", "L3", "L4", "L5", "L2"],
        ["L4", "L5", "L3", "L4", "L2"],
      ]);

      const end = fx.spinBoundaryEnd("base:1", "500");
      e.action(end.type, end.payload, { actionPhaseName: end.phase, debugTag: "end" });
    })
    .build();
}
